import { Preferences } from '@capacitor/preferences';
import { BMT_DRAFT_KEY_PREFIX } from '@/constants/bmt';

/**
 * Unsent TEXT homework answers, kept on this device until they are submitted.
 * Capacitor Preferences is native storage on iOS/Android and localStorage on web.
 * A failed read or write is never fatal: losing a draft is better than a screen
 * that will not open.
 */

export const draftKey = homeworkId => `${BMT_DRAFT_KEY_PREFIX}${homeworkId}`;

export async function loadDraft(homeworkId) {
  try {
    const { value } = await Preferences.get({ key: draftKey(homeworkId) });
    return value || '';
  } catch {
    return '';
  }
}

export async function saveDraft(homeworkId, text) {
  try {
    if (text) await Preferences.set({ key: draftKey(homeworkId), value: text });
    else await Preferences.remove({ key: draftKey(homeworkId) });
  } catch {
    // Storage unavailable (private browsing, quota): the answer still submits.
  }
}

export const clearDraft = homeworkId => saveDraft(homeworkId, '');

/** Every BMT draft on this device: an unsent answer must not outlive sign-out. */
export async function clearAllDrafts() {
  try {
    const { keys } = await Preferences.keys();
    const drafts = keys.filter(key => key.startsWith(BMT_DRAFT_KEY_PREFIX));
    await Promise.all(drafts.map(key => Preferences.remove({ key })));
  } catch {
    // Storage unavailable: nothing was saved there either.
  }
}

/** The testimony's draft is its four sections, kept as one JSON string. */
export function encodeSections(sections = []) {
  return sections.some(part => part && part.trim()) ? JSON.stringify(sections) : '';
}

/**
 * Read a testimony draft back as four sections. A draft saved before sections
 * existed (plain text) becomes the first section, so nothing typed is lost.
 */
export function decodeSections(value, count = 4) {
  const empty = Array.from({ length: count }, () => '');
  if (!value) return empty;
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return empty.map((_, i) => (typeof parsed[i] === 'string' ? parsed[i] : ''));
    }
  } catch {
    // Not JSON: an older plain-text draft.
  }
  return [String(value), ...empty.slice(1)];
}
